import React, { useEffect, useState, useRef, useCallback } from "react";
import Modal from "./Modal";
import OverlapConfirmDialog from "./OverlapConfirmDialog";
import PhoneInput, { phoneDigitsValid } from "./PhoneInput";
import { api, formatMXN, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, UserPlus } from "lucide-react";

const todayPlus = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const SLOTS_DEBOUNCE_MS = 300;

const REASON_MESSAGES = {
  sin_horario: "El doctor no atiende ese día.",
  bloqueado: "El doctor no está disponible ese día.",
  fecha_pasada: "Elige una fecha futura.",
  sin_huecos: "No quedan horarios libres ese día.",
};

const blankForm = (defaultPatient) => ({
  patient_name: defaultPatient?.name || "",
  patient_phone: defaultPatient?.phone || "",
  patient_id: defaultPatient?.id || null,
  doctor_id: "",
  service_id: "",
  date: todayPlus(1),
  time: "",
  notes: "",
});

export default function NewAppointmentModal({ open, onClose, onCreated, defaultPatient, editingAppointment }) {
  const isEdit = !!editingAppointment;
  const [doctors, setDoctors] = useState([]);
  const [doctorServices, setDoctorServices] = useState(null); // null = loading, [] = no services
  const [patientMode, setPatientMode] = useState("existing"); // "existing" | "new"
  const [patientSearch, setPatientSearch] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [form, setForm] = useState(() => blankForm(defaultPatient));
  // Fecha de nacimiento del paciente NUEVO: va solo en el POST /patients (PatientCreate la exige
  // desde 43a3c7a). Fuera de `form` a proposito, para que no viaje en el payload de la cita.
  const [newPatientBirthDate, setNewPatientBirthDate] = useState("");
  const [saving, setSaving] = useState(false);
  const [overlapConfirm, setOverlapConfirm] = useState(null); // { payload, detail } | null
  const [overlapSubmitting, setOverlapSubmitting] = useState(false);
  const [slotsState, setSlotsState] = useState({ loading: false, slots: [], reason: null, error: null });
  const searchRef = useRef(null);
  const slotsAbortRef = useRef(null);
  const slotsDebounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setDoctorServices(null);
    setSlotsState({ loading: false, slots: [], reason: null, error: null });
    setOverlapConfirm(null);
    setOverlapSubmitting(false);
    setNewPatientBirthDate("");

    if (isEdit) {
      setForm({
        patient_name: editingAppointment.patient_name || "",
        patient_phone: editingAppointment.patient_phone || "",
        patient_id: editingAppointment.patient_id || null,
        doctor_id: editingAppointment.doctor_id || "",
        service_id: editingAppointment.service_id != null ? String(editingAppointment.service_id) : "",
        date: editingAppointment.date,
        time: editingAppointment.time,
        notes: editingAppointment.notes || "",
      });
      setPatientMode("existing");
      setPatientSearch(editingAppointment.patient_name || "");
    } else {
      setForm(blankForm(defaultPatient));
      if (defaultPatient?.id) {
        setPatientMode("existing");
        setPatientSearch(defaultPatient.name || "");
      } else {
        setPatientMode("existing");
        setPatientSearch("");
      }
    }

    api.get("/doctors").then((d) => {
      setDoctors(d.data.items);
      if (!isEdit) {
        setForm((f) => ({ ...f, doctor_id: f.doctor_id || d.data.items[0]?.id || "" }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultPatient, editingAppointment]);

  // Fetch doctor services when doctor changes. Si el service_id actual sigue siendo válido para
  // el nuevo doctor (caso: precarga en modo edición), lo conserva; si no, cae al primero.
  useEffect(() => {
    if (!form.doctor_id) { setDoctorServices(null); return; }
    setDoctorServices(null);
    api.get(`/doctor-services/${form.doctor_id}`)
      .then((r) => {
        const items = r.data.items;
        setDoctorServices(items);
        setForm((f) => {
          const stillValid = items.some((it) => String(it.id) === String(f.service_id));
          return { ...f, service_id: stillValid ? f.service_id : String(items[0]?.id ?? "") };
        });
      })
      .catch(() => setDoctorServices([]));
  }, [form.doctor_id]);

  // Disponibilidad real (S5c): pide slots al backend cuando hay doctor + fecha. Debounce +
  // AbortController para que un cambio rápido de doctor/fecha no pinte horarios del anterior.
  useEffect(() => {
    if (!open) return;
    if (slotsDebounceRef.current) clearTimeout(slotsDebounceRef.current);
    if (!form.doctor_id || !form.date) {
      setSlotsState({ loading: false, slots: [], reason: null, error: null });
      return;
    }
    setSlotsState((s) => ({ ...s, loading: true, error: null }));
    slotsDebounceRef.current = setTimeout(() => {
      if (slotsAbortRef.current) slotsAbortRef.current.abort();
      const controller = new AbortController();
      slotsAbortRef.current = controller;
      const params = { date: form.date };
      if (form.service_id) params.service_id = form.service_id;
      if (isEdit) params.exclude_appointment_id = editingAppointment.id;
      api.get(`/doctors/${form.doctor_id}/slots`, { params, signal: controller.signal })
        .then(({ data }) => {
          const day = data.days?.[0] || { slots: [], reason: null };
          setSlotsState({ loading: false, slots: day.slots || [], reason: day.reason, error: null });
          setForm((f) => (f.time && !(day.slots || []).includes(f.time) ? { ...f, time: "" } : f));
        })
        .catch((e) => {
          if (e.code === "ERR_CANCELED" || e.name === "CanceledError") return;
          setSlotsState({ loading: false, slots: [], reason: null, error: formatApiError(e?.response?.data?.detail) });
        });
    }, SLOTS_DEBOUNCE_MS);
    return () => clearTimeout(slotsDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.doctor_id, form.date, form.service_id, isEdit]);

  const displayedServices = (doctorServices || []).map((ds) => ({
    id: ds.id,
    name: ds.name,
    duration: ds.duration_minutes,
    price: ds.price,
  }));

  // Patient search debounce
  const searchTimeout = useRef(null);
  const handlePatientSearch = useCallback((q) => {
    setPatientSearch(q);
    setForm((f) => ({ ...f, patient_name: q, patient_id: null }));
    clearTimeout(searchTimeout.current);
    if (!q.trim()) { setPatientResults([]); setShowDropdown(false); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/patients", { params: { q } });
        setPatientResults(data.items.slice(0, 8));
        setShowDropdown(true);
      } catch { setPatientResults([]); }
    }, 250);
  }, []);

  const selectPatient = (p) => {
    setPatientSearch(p.name);
    setForm((f) => ({ ...f, patient_name: p.name, patient_phone: p.phone || f.patient_phone, patient_id: p.id }));
    setShowDropdown(false);
    setPatientResults([]);
  };

  // Payload de la cita en si (sin allow_overlap -- se agrega en sendAppointment). Se recalcula
  // tanto en el envio original como al armar el reenvio con allow_overlap:true (PASO 1), asi
  // patient_id ya resuelto (paciente nuevo creado o no) nunca se re-crea dos veces.
  const buildAppointmentPayload = (patient_id) =>
    isEdit
      ? { doctor_id: form.doctor_id, service_id: form.service_id, date: form.date, time: form.time, notes: form.notes }
      : { ...form, patient_id };

  const sendAppointment = (payload, allowOverlap) => {
    const body = { ...payload, allow_overlap: allowOverlap };
    return isEdit ? api.patch(`/appointments/${editingAppointment.id}`, body) : api.post("/appointments", body);
  };

  const isOverlapError = (e) => e?.response?.status === 409 && e?.response?.data?.code === "APPOINTMENT_OVERLAP";

  const submit = async () => {
    if (!isEdit) {
      if (patientMode === "existing" && !form.patient_id && !form.patient_name) {
        toast.error("Selecciona o escribe el nombre del paciente");
        return;
      }
      if (patientMode === "new" && !form.patient_name) {
        toast.error("Escribe el nombre del paciente nuevo");
        return;
      }
      // La regla de 10 digitos es de CAPTURA HUMANA: solo aplica donde hay un PhoneInput
      // visible (modo "new"). En modo "existing" no se muestra ningun campo de telefono: el
      // valor es el guardado del paciente (a menudo "+52..."), que el backend ya acepta y
      // normaliza (normalize_phone, 10/12/13 digitos). Validarlo aqui bloqueaba la cita.
      if (patientMode === "new" && !phoneDigitsValid(form.patient_phone, false)) {
        toast.error("El teléfono debe tener 10 dígitos, o déjalo vacío");
        return;
      }
      if (patientMode === "new" && !newPatientBirthDate) {
        toast.error("La fecha de nacimiento es obligatoria");
        return;
      }
    }
    if (!form.doctor_id) {
      toast.error("Selecciona un doctor");
      return;
    }
    if (!form.service_id) {
      toast.error("Selecciona un servicio");
      return;
    }
    if (!form.time) {
      toast.error("Selecciona un horario");
      return;
    }
    setSaving(true);
    let patient_id = form.patient_id;
    try {
      if (!isEdit && patientMode === "new" && !patient_id) {
        const { data: newPat } = await api.post("/patients", {
          name: form.patient_name,
          phone: form.patient_phone || "",
          birth_date: newPatientBirthDate,
          source: "Recepción",
        });
        patient_id = newPat.id;
        setForm((f) => ({ ...f, patient_id }));
      }
      const { data } = await sendAppointment(buildAppointmentPayload(patient_id), false);
      toast.success(isEdit ? "Cita actualizada" : `Cita creada para ${data.patient_name}`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      if (isOverlapError(e)) {
        // No mostrar el error generico: PASO 1 pide una decision, no un toast.
        setOverlapConfirm({ payload: buildAppointmentPayload(patient_id), detail: e.response.data.detail });
      } else {
        const msg = e?.response?.data?.detail;
        toast.error(formatApiError(msg) || (isEdit ? "No se pudo actualizar la cita" : "No se pudo crear la cita"));
      }
    } finally {
      setSaving(false);
    }
  };

  const cancelOverlap = () => setOverlapConfirm(null); // "Cambiar horario" -- el form sigue intacto, no se toco `form`.

  const confirmOverlap = async () => {
    if (!overlapConfirm) return;
    setOverlapSubmitting(true);
    try {
      const { data } = await sendAppointment(overlapConfirm.payload, true);
      toast.success(isEdit ? "Cita actualizada (empalmada a propósito)" : `Cita creada para ${data.patient_name} (empalmada a propósito)`);
      onCreated?.(data);
      setOverlapConfirm(null);
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(formatApiError(msg) || "No se pudo agendar la cita");
    } finally {
      setOverlapSubmitting(false);
    }
  };

  const selectedService = form.service_id ? displayedServices.find((s) => String(s.id) === String(form.service_id)) : null;

  const hourFieldDisabled = !form.doctor_id || !form.date;

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Editar cita" : "Crear nueva cita"}
      size="lg"
      testid="modal-new-appointment"
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50" data-testid="appt-cancel">
            Cancelar
          </button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-brand-electric text-white hover:bg-blue-700 disabled:opacity-60" data-testid="appt-submit">
            {saving ? (isEdit ? "Guardando…" : "Creando…") : (isEdit ? "Guardar cambios" : "Crear cita")}
          </button>
        </>
      }
    >
      {/* Patient mode toggle -- oculto en edición: reagendar no cambia el paciente (el backend
          tampoco lo acepta en el PATCH). */}
      {!isEdit && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Paciente</span>
          <div className="inline-flex rounded-lg overflow-hidden border border-slate-200 text-xs ml-2">
            <button
              type="button"
              onClick={() => { setPatientMode("existing"); setPatientSearch(""); setNewPatientBirthDate(""); setForm((f) => ({ ...f, patient_name: "", patient_phone: "", patient_id: null })); }}
              className={`px-3 py-1.5 ${patientMode === "existing" ? "bg-brand-electric text-white" : "bg-white text-slate-600"}`}
            >
              Paciente existente
            </button>
            <button
              type="button"
              onClick={() => { setPatientMode("new"); setPatientSearch(""); setNewPatientBirthDate(""); setForm((f) => ({ ...f, patient_name: "", patient_phone: "", patient_id: null })); }}
              className={`px-3 py-1.5 ${patientMode === "new" ? "bg-brand-electric text-white" : "bg-white text-slate-600"}`}
            >
              <UserPlus className="w-3 h-3 inline mr-1" />Nuevo paciente
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {isEdit ? (
          <div className="sm:col-span-2">
            <Field label="Paciente">
              <div className="input bg-slate-50 text-slate-700">{form.patient_name || "—"}</div>
            </Field>
          </div>
        ) : patientMode === "existing" ? (
          <div className="sm:col-span-2 relative" ref={searchRef}>
            <Field label="Buscar paciente">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={patientSearch}
                  onChange={(e) => handlePatientSearch(e.target.value)}
                  onFocus={() => patientResults.length > 0 && setShowDropdown(true)}
                  placeholder="Nombre o teléfono del paciente…"
                  className="input"
                  style={{ paddingLeft: '2.25rem' }}
                  data-testid="appt-patient-search"
                  autoComplete="off"
                />
              </div>
              {showDropdown && patientResults.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-52 overflow-y-auto">
                  {patientResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onMouseDown={() => selectPatient(p)}
                      className="w-full text-left px-4 py-2.5 hover:bg-slate-50 text-sm flex items-center gap-3"
                    >
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 grid place-items-center text-xs font-semibold shrink-0">
                        {p.name[0]}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{p.name}</div>
                        <div className="text-xs text-slate-500">{p.phone}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Field>
            {form.patient_id && (
              <div className="mt-1 text-xs text-emerald-600 font-medium">✓ Paciente seleccionado</div>
            )}
          </div>
        ) : (
          <>
            <Field label="Nombre del paciente nuevo">
              <input
                value={form.patient_name}
                onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                placeholder="Andrea Romero"
                data-testid="appt-patient-name"
                className="input"
              />
            </Field>
            <Field label="Teléfono / WhatsApp">
              <PhoneInput
                value={form.patient_phone}
                onChange={(digits) => setForm({ ...form, patient_phone: digits })}
                testId="appt-patient-phone"
              />
            </Field>
            {/* Mismo campo que NewPatientModal.js. Envuelto para que ocupe su propia fila y
                Doctor/Servicio sigan juntos debajo. */}
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Fecha de nacimiento">
                <input
                  type="date"
                  className="input"
                  max={new Date().toISOString().slice(0, 10)}
                  value={newPatientBirthDate}
                  onChange={(e) => setNewPatientBirthDate(e.target.value)}
                  data-testid="appt-patient-birth-date"
                />
              </Field>
            </div>
          </>
        )}

        <Field label="Doctor">
          <select value={form.doctor_id} onChange={(e) => setForm({ ...form, doctor_id: e.target.value, time: "" })} className="input" data-testid="appt-doctor">
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>{d.name} — {d.specialty}</option>
            ))}
          </select>
        </Field>
        <Field label="Servicio">
          {!form.doctor_id ? (
            <div className="input text-slate-400 text-sm">Selecciona un doctor primero</div>
          ) : doctorServices === null ? (
            <div className="input text-slate-400 text-sm">Cargando servicios…</div>
          ) : doctorServices.length === 0 ? (
            <select value="" disabled className="input text-slate-400" data-testid="appt-service">
              <option value="">Sin servicios disponibles</option>
            </select>
          ) : (
            <select value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} className="input" data-testid="appt-service">
              {displayedServices.map((s) => (
                <option key={s.id} value={s.id}>{s.name} — {s.duration} min — {formatMXN(s.price)}</option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Fecha">
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value, time: "" })} className="input" data-testid="appt-date" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Hora">
            {hourFieldDisabled ? (
              <div className="input text-slate-400 text-sm">Elige doctor y fecha para ver horarios disponibles</div>
            ) : slotsState.loading ? (
              <div className="input text-slate-400 text-sm">Cargando horarios…</div>
            ) : slotsState.error ? (
              <div className="input text-red-600 text-sm border border-red-300 rounded-lg">{slotsState.error}</div>
            ) : slotsState.slots.length === 0 ? (
              <div className="input text-slate-400 text-sm">{REASON_MESSAGES[slotsState.reason] || "No hay horarios disponibles."}</div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="appt-time-slots">
                {slotsState.slots.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, time: s }))}
                    className={`px-3 py-1.5 rounded-lg text-sm border font-mono transition-colors ${
                      form.time === s
                        ? "bg-brand-electric text-white border-brand-electric"
                        : "bg-white text-slate-700 border-slate-300 hover:border-brand-electric"
                    }`}
                    data-testid={`appt-slot-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Notas">
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notas internas" className="input" data-testid="appt-notes" />
          </Field>
        </div>
      </div>
      {selectedService && (
        <div className="mt-4 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
          <span className="font-semibold text-slate-700">Resumen:</span> {selectedService.name} · {selectedService.duration} min · {formatMXN(selectedService.price)}
        </div>
      )}
      <style>{`.input { width:100%; border:1px solid #cbd5e1; background:#fff; border-radius:8px; padding:.55rem .75rem; font-size:.875rem; outline:none; } .input:focus { border-color:#2563EB; box-shadow:0 0 0 2px rgba(37,99,235,.18); }`}</style>
    </Modal>
    <OverlapConfirmDialog
      open={!!overlapConfirm}
      detail={overlapConfirm?.detail}
      confirming={overlapSubmitting}
      onCancel={cancelOverlap}
      onConfirm={confirmOverlap}
    />
    </>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
